/**
 * Chat timeline model: merges approval comments and approval history events
 * into one chronological list for the Release drawer.
 */

import type { ApprovalComment, ApprovalHistoryEntry } from '@/types/approval';

export interface TimelineMessage {
  kind: 'message';
  key: string;
  at: number;
  comment: ApprovalComment;
  /** The comment this one replies to (for the quoted snippet) */
  parent: ApprovalComment | null;
}

export interface TimelineEvent {
  kind: 'event';
  key: string;
  at: number;
  entry: ApprovalHistoryEntry;
  /** Show the review comment inside the event card */
  showComment: boolean;
  /** The mirrored chat comment of this decision (attachments, mentions, reply target) */
  twin: ApprovalComment | null;
}

export type TimelineItem = TimelineMessage | TimelineEvent;

interface BuildTimelineInput {
  comments: ApprovalComment[] | undefined;
  history: ApprovalHistoryEntry[] | undefined;
  variationId: number | null;
  /** Hide internal ("Internal team only") notes (reviewer-only people) */
  hideInternal: boolean;
}

/**
 * History pills are scoped EXACTLY by version. `undefined` (field not sent)
 * counts as unknown and only shows on the Original (fail closed).
 */
const historyMatchesVersion = (
  entryVariationId: number | null | undefined,
  variationId: number | null
): boolean => (entryVariationId === undefined ? null : entryVariationId) === variationId;

/** Flatten top-level comments + nested replies, de-duplicated by id. */
const flattenComments = (comments: ApprovalComment[]): ApprovalComment[] => {
  const byId = new Map<string, ApprovalComment>();
  const visit = (comment: ApprovalComment): void => {
    if (!byId.has(comment.id)) byId.set(comment.id, comment);
    comment.replies?.forEach(visit);
  };
  comments.forEach(visit);
  return [...byId.values()];
};

const toMillis = (iso: string): number => {
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Normalise text for the mirrored-bubble match: strip invisible zero-width
 * characters (U+200B/C/D, U+FEFF), collapse whitespace runs, trim.
 */
const normaliseForMatch = (text: string): string =>
  text
    .replace(/\u200B|\u200C|\u200D|\uFEFF/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export function buildTimeline({
  comments,
  history,
  variationId,
  hideInternal,
}: BuildTimelineInput): TimelineItem[] {
  // Comments arrive already scoped to the version on screen (per-version thread
  // endpoint), so only the internal-note filter applies here.
  const flat = flattenComments(comments ?? []).filter((c) => !(hideInternal && c.isInternal));
  const byId = new Map(flat.map((c) => [c.id, c]));

  // History events of THIS version, oldest first.
  const versionHistory = (history ?? [])
    .filter((entry) => historyMatchesVersion(entry.variationId, variationId))
    .sort((x, y) => toMillis(x.timestamp) - toMillis(y.timestamp));

  // A review comment (e.g. "Changes requested") is also stored as a chat
  // message. Show it ONCE inside the event card, and hide the mirrored bubble.
  // There is no link between the two rows, so match one-to-one by author +
  // text, choosing the CLOSEST comment within a day of the decision (seeded and
  // imported rows can be minutes apart, live ones are seconds apart).
  const hiddenCommentIds = new Set<string>();
  const twinByEntryId = new Map<string, ApprovalComment>();
  const MIRROR_WINDOW_MS = 24 * 60 * 60 * 1000;
  for (const entry of versionHistory) {
    const trimmed = normaliseForMatch(entry.comment ?? '');
    if (trimmed.length === 0) continue;
    const at = toMillis(entry.timestamp);
    let twin: ApprovalComment | null = null;
    let twinDistance = MIRROR_WINDOW_MS;
    for (const c of flat) {
      if (hiddenCommentIds.has(c.id) || c.userId !== entry.userId) continue;
      if (normaliseForMatch(c.content) !== trimmed) continue;
      const distance = Math.abs(toMillis(c.createdAt) - at);
      if (distance <= twinDistance) {
        twin = c;
        twinDistance = distance;
      }
    }
    if (twin) {
      hiddenCommentIds.add(twin.id);
      twinByEntryId.set(entry.id, twin);
    }
  }

  const messages: TimelineMessage[] = flat
    .filter((comment) => !hiddenCommentIds.has(comment.id))
    .map((comment) => ({
      kind: 'message',
      key: `c-${comment.id}`,
      at: toMillis(comment.createdAt),
      comment,
      parent: comment.parentId ? (byId.get(comment.parentId) ?? null) : null,
    }));

  // The backend records every submission as SUBMITTED. Any submission after an
  // earlier submission of the same version is shown as "sent for review again".
  let submittedBefore = false;
  const events: TimelineEvent[] = versionHistory.map((entry) => {
    let shown = entry;
    if (entry.action === 'SUBMITTED' || entry.action === 'RESUBMITTED') {
      if (submittedBefore && entry.action === 'SUBMITTED') {
        shown = { ...entry, action: 'RESUBMITTED' };
      }
      submittedBefore = true;
    }
    // A review decision implies the item was sent before (history may start late).
    if (entry.action === 'CHANGES_REQUESTED' || entry.action === 'APPROVED') submittedBefore = true;
    return {
      kind: 'event',
      key: `h-${entry.id}`,
      at: toMillis(entry.timestamp),
      entry: shown,
      showComment: (entry.comment?.trim() ?? '').length > 0,
      twin: twinByEntryId.get(entry.id) ?? null,
    };
  });

  return [...messages, ...events].sort((a, b) => a.at - b.at);
}

/**
 * Calendar day number (days since epoch) of an instant in the given IANA
 * timezone. Differences are exact calendar-day counts, DST safe.
 */
export function calendarDayNumber(date: Date, timezone: string): number {
  // en-CA formats as YYYY-MM-DD
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: timezone })
    .format(date)
    .split('-')
    .map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
}
